import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import {
    Box,
    TextField,
    FormControl,
    Grid,
    Button,
    Tooltip,
} from "@mui/material";
import { GetCustomEarningAction } from "../../../../../../store/Finance/Component/Action";
import { AddLeaveWageAction, UpdateLeaveWageAction } from "../../../../../../store/Finance/PayRollTemplate/Action";
import SelectableSearch from "../../../../Base/views/SelectableSearch";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';


const LeaveWage = ({ editedRecord, setOpenPopup }) => {
    const dispatch = useDispatch();
    const initialValue = {
        calculation_earning_components: [],
        standard_paid_days: "",
        leave_eligibility_days: "",
    };

    const [leaveWage, setLeaveWage] = useState(initialValue);
    const [errors, setErrors] = useState({});
    const [earningData, setEarningData] = useState({
        load: true,
        values: [],
    });
    const { custom_earning_data } = useSelector(
        (state) => state?.FinanceReducer.ComponentReducer
    );
    useEffect(() => {
        if (editedRecord != null) {
            setLeaveWage({
                ...editedRecord,
                calculation_earning_components: editedRecord?.calculation_earning_components.length > 0 ?
                    editedRecord?.calculation_earning_components : [],
            });
        }
        dispatch(GetCustomEarningAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedRecord]);
    const handleEarnings = () => {
        custom_earning_data &&
            setEarningData({ load: false, values: custom_earning_data?.[0]?.earnings_json });
    };
    const validate = (fieldValues = leaveWage) => {
        let temp = { ...errors };

        if ("standard_paid_days" in fieldValues)
            temp.standard_paid_days = fieldValues.standard_paid_days ? "" : "Standard paid days is required.";

        if ("leave_eligibility_days" in fieldValues)
            temp.leave_eligibility_days = fieldValues.leave_eligibility_days ? "" : "Statutory leave eligibility days is required.";

        if ("calculation_earning_components" in fieldValues)
            temp.calculation_earning_components =
                fieldValues.calculation_earning_components?.length === 0
                    ? "Earning is required"
                    : fieldValues.calculation_earning_components === null
                        ? "Earning is required"
                        : "";

        setErrors({ ...temp });

        return Object.values(temp).every((x) => x === "");
    };
    const handleInput = (event, value) => {

        if (["calculation_earning_components"].includes(event)) {
            if (value) {
                setLeaveWage({ ...leaveWage, [event]: value });
            } else {
                setLeaveWage({ ...leaveWage, [event]: "" });
            }
            Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
                validate({ ...leaveWage, [event]: value ? value : "" });
        }
        else {
            setLeaveWage({
                ...leaveWage,
                [event.target.name]: event.target.value,
            });
            Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
                validate({
                    ...leaveWage,
                    [event.target.name]: event.target.value,
                })
        }

    };
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (validate()) {
            const transformedVariableEarningComponents = 
                leaveWage?.calculation_earning_components?.map((item) => ({
                    name: item.name,
                    label: item.label,
                }));
            
            if (editedRecord?.leave_wages_configuration_id) {
                dispatch(UpdateLeaveWageAction({
                    ...leaveWage,
                    calculation_earning_components: leaveWage?.calculation_earning_components?.length >= 1 ? transformedVariableEarningComponents || [] : [],
                    leave_wages_configuration_id: editedRecord?.leave_wages_configuration_id
                }));
            }
            else {
                dispatch(
                    AddLeaveWageAction({
                        ...leaveWage,
                        calculation_earning_components: leaveWage?.calculation_earning_components?.length >= 1 ? transformedVariableEarningComponents || [] : [],
                    })
                );
            }
            setOpenPopup(false);
        }
    };
    return (
        <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                    <FormControl fullWidth>
                        <SelectableSearch
                            isOptionEqualToValue={(option, value) =>
                                option.label === value.label
                            }
                            multiple={true}
                            limitTags={1}
                            label="Select an Earnings"
                            fullWidth
                            value={leaveWage?.calculation_earning_components}
                            loading={() => handleEarnings()}
                            fieldLabel="label"
                            required={true}
                            variantType="outlined"
                            name="calculation_earning_components"
                            size="small"
                            onChangeEvent={handleInput}
                            data={earningData}
                            {...(errors.calculation_earning_components && {
                                error: true,
                                helperText: errors.calculation_earning_components,
                            })}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        size="small"
                        required
                        type="number"
                        value={leaveWage?.standard_paid_days}
                        onChange={handleInput}
                        variant="outlined"
                        fullWidth
                        id="standard_paid_days"
                        label="Standard Paid Days"
                        name="standard_paid_days"
                        autoComplete="standard_paid_days"
                        InputProps={{
                            endAdornment: (
                                <Tooltip title="Fixed number of days (e.g. 26) used to calculate daily wages. Remains constant each month for consistent payroll.">
                                    <InfoOutlinedIcon fontSize="small" color="action" style={{ cursor: "pointer" }} />
                                </Tooltip>
                            ),
                        }}
                        {...(errors.standard_paid_days && {
                            error: true,
                            helperText: errors.standard_paid_days,
                        })}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        size="small"
                        required
                        type="number"
                        value={leaveWage?.leave_eligibility_days}
                        onChange={handleInput}
                        variant="outlined"
                        fullWidth
                        id="leave_eligibility_days"
                        label="Statutory Leave Eligibility Days"
                        name="leave_eligibility_days"
                        autoComplete="leave_eligibility_days"
                        InputProps={{
                            endAdornment: (
                                <Tooltip title="Number of days (e.g. 20) an employee must work to earn one leave day, as per labor laws.">
                                    <InfoOutlinedIcon fontSize="small" color="action" style={{ cursor: "pointer" }} />
                                </Tooltip>
                            ),
                        }}
                        {...(errors.leave_eligibility_days && {
                            error: true,
                            helperText: errors.leave_eligibility_days,
                        })}
                    />
                </Grid>
            </Grid>
            <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                    type="submit"
                    variant="contained"
                    size="medium"
                    className="mpl-primary-btn"
                >
                    {editedRecord ? "Update" : "Save"}
                </Button>
            </Box>
        </Box>
    );
};
export default LeaveWage;
